import { supabase } from './supabase'

export interface AuthUser {
  id: string
  email: string
  role: 'admin' | 'customer'
  profile?: any
}

export const authService = {
  async signIn(email: string, password: string): Promise<{ userRole: string; userId: string, userFullName: string; userEmail: string }> {
    // Use Supabase Auth for authentication
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (authError) {
      throw new Error(authError.message);
    }

    if (!authData.user) {
      throw new Error('Authentication failed');
    }

    // Check if user is an admin using the authenticated user's ID
    const { data: adminData, error: adminError } = await supabase
      .from('admins')
      .select('id, name, email, role')
      .eq('id', authData.user.id)
      .maybeSingle();

    if (adminData && !adminError) {
      return {
        userRole: 'admin',
        userId: adminData.id,
        userFullName: adminData.name,
        userEmail: adminData.email
      };
    }

    // Check if user is a customer using the authenticated user's ID
    const { data: customerData, error: customerError } = await supabase
      .from('customers')
      .select('id, first_name, last_name, email')
      .eq('id', authData.user.id)
      .maybeSingle();

    if (customerData && !customerError) {
      return {
        userRole: 'customer',
        userId: customerData.id,
        userFullName: `${customerData.first_name} ${customerData.last_name}`,
        userEmail: customerData.email
      };
    }

    // If user exists in auth but not in our tables, sign them out
    await supabase.auth.signOut();
    throw new Error('User not found in system. Please contact administrator.');
  },

  async signUp(email: string, password: string, userData: any, userType: 'admin' | 'customer') {
    // Sign up with Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password
    });

    if (authError) {
      throw new Error(authError.message);
    }

    if (!authData.user) {
      throw new Error('Sign up failed');
    }

    // Create user record in appropriate table
    if (userType === 'admin') {
      const { data, error } = await supabase
        .from('admins')
        .insert([{
          ...userData,
          email,
          id: authData.user.id
        }])
        .select()
        .single();

      if (error) {
        // If user creation fails, clean up auth user
        await supabase.auth.admin.deleteUser(authData.user.id);
        throw new Error('Failed to create admin profile');
      }

      return data;
    } else {
      const { data, error } = await supabase
        .from('customers')
        .insert([{
          ...userData,
          email,
          id: authData.user.id
        }])
        .select()
        .single();

      if (error) {
        // If user creation fails, clean up auth user
        await supabase.auth.admin.deleteUser(authData.user.id);
        throw new Error('Failed to create customer profile');
      }

      return data;
    }
  },

  async signOut() {
    await supabase.auth.signOut();
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
  },

  async getCurrentUser(): Promise<AuthUser | null> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      localStorage.removeItem('userRole');
      localStorage.removeItem('userId');
      return null;
    }

    // Check if user is an admin
    const { data: adminData } = await supabase
      .from('admins')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    if (adminData) {
      localStorage.setItem('userRole', 'admin');
      localStorage.setItem('userId', adminData.id);
      return {
        id: adminData.id,
        email: adminData.email,
        role: 'admin',
        profile: adminData
      };
    }

    // Check if user is a customer
    const { data: customerData } = await supabase
      .from('customers')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();

    if (customerData) {
      localStorage.setItem('userRole', 'customer');
      localStorage.setItem('userId', customerData.id);
      return {
        id: customerData.id,
        email: customerData.email,
        role: 'customer',
        profile: customerData
      };
    }

    // User exists in auth but not in our tables
    await supabase.auth.signOut();
    localStorage.removeItem('userRole');
    localStorage.removeItem('userId');
    return null;
  },

  async resetPassword(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`
    });

    if (error) {
      throw new Error(error.message);
    }
  },

  async updatePassword(newPassword: string) {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      throw new Error(error.message);
    }
  }
}